import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useRibbon } from './useRibbon';
import styles from './Ribbon.module.css';
import { HistoryContext, history } from '@src/providers/HistoryProvider/HistoryProvider';
import { IconButton, ITheme, mergeStyles, mergeStyleSets, ThemeProvider, useTheme } from '@fluentui/react';
import { CommandBar } from '@talxis/react-components';
import { Text } from '@fluentui/react/lib/Text';
import { Ribbon } from '@src/app/classes/models/Ribbon/Ribbon';
import { ICommandBarItemProps } from '@talxis/react-components/dist/components/CommandBar/CommandBar.types';
import { ThemeDefinition } from '@src/app/classes/definitions/ThemeDefinition';

export interface RibbonControllerProps {
    ribbon?: Ribbon;
    className?: string;
    commandBarClassName?: string;
    theme?: ITheme;
    showBackButton?: boolean;
    forceHidden?: boolean;
    hasUnsavedChanges?: boolean;
    additionalCommands?: ICommandBarItemProps[];
}

const Loading = () => {
    const theme = useTheme();
    const themedStyles = mergeStyleSets({
        loadingLine: {
            background: `linear-gradient(to right, color-mix(in oklab, ${theme.palette.white}, ${theme.palette.black} 8%) 4%, color-mix(in oklab, ${theme.palette.white}, ${theme.palette.black} 5%) 25%, color-mix(in oklab, ${theme.palette.white}, ${theme.palette.black} 8%) 36%)`
        }
    });
    return (
        <div className={styles.loading}>
            {Array.from(Array(4).keys()).map((x) =>
                <div key={x} className={`${styles.loadingLine} ${themedStyles.loadingLine}`} />
            )}
        </div>
    );
};

const BackButton: React.FC = () => {
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const historyContext = useContext(HistoryContext);
    const theme = useTheme();
    const className = useMemo(() => {
        return `TALXIS__navigate-back-btn ${mergeStyles({
            height: 44,
            width: 44,
            '&[data-disabled="true"]': {
                pointerEvents: 'none',
                'i': {
                    color: theme.semanticColors.inputIconDisabled
                }
            },
            borderRight: `1px solid ${theme.semanticColors.bodyDivider}`
        })}`;
    }, [isDisabled]);

    useEffect(() => {
        if (historyContext?.currentPage.isFirst()) {
            setIsDisabled(true);
        }
        else {
            setIsDisabled(false);
        }
    }, [historyContext?.currentPage]);
    return (
        <IconButton data-disabled={isDisabled} className={`${className} ${styles.backButton}`} onClick={() => history.goBack()} iconProps={{
            iconName: 'Back',
        }} />
    );
};

export const RibbonController = ({ ribbon, className, commandBarClassName, theme, showBackButton, forceHidden, hasUnsavedChanges, additionalCommands }: RibbonControllerProps) => {
    const ribbonTheme = theme ?? useTheme();
    const commands = useRibbon(ribbon);
    const finalCommands = useMemo(() => {
        if (additionalCommands?.length > 0 && commands) {
            return [...additionalCommands, ...commands];
        }
        return commands;
    }, [additionalCommands, commands]);

    if (forceHidden) {
        return <></>;
    }
    if (!ribbon) {
        return <ThemeProvider theme={ribbonTheme} className={`TALXIS__ribbon ${styles.root}${className ? ` ${className}` : ''}`}>
            {showBackButton &&
                <BackButton />
            }
            <Loading />
        </ThemeProvider>;
    }
    return (
        <>
            {/* if ribbon has been fully loaded or is in a process of initial load */}
            {ribbon.isEnabled() && (finalCommands?.length > 0 || finalCommands === null || showBackButton) &&
                <ThemeProvider theme={ribbonTheme} className={`TALXIS__ribbon ${styles.root}${className ? ` ${className}` : ''}`}>
                    {showBackButton &&
                        <BackButton />
                    }
                    {!finalCommands &&
                        <Loading />
                    }
                    {finalCommands &&
                        <>
                            <CommandBar
                                contextualMenuTheme={ThemeDefinition.get().main}
                                className={`${styles.commands}${commandBarClassName ? ` ${commandBarClassName}` : ''}`}
                                items={finalCommands.filter(command => !command["farItem"])}
                                farItems={finalCommands.filter(command => command["farItem"])} />
                            {hasUnsavedChanges &&
                                <Text
                                    styles={{
                                        root: {
                                            color: ribbonTheme.semanticColors.buttonTextDisabled
                                        }
                                    }}
                                    className={styles.unsavedChanges}>{window.TALXIS.Portal.Translations.getLocalizedString('components/dialogs/NavigationPromptDialog/Title')}</Text>
                            }
                        </>

                    }
                </ThemeProvider>
            }
        </>
    );
};